// Import react.
import * as React from 'react';
// Import services.
import {
	StringService,
} from '@app/services';
// Import material ui modules
import {
	Domain,
} from '@mui/icons-material';
// Import props.
import {
	InputCurrencyWithoutInputTextBoxProps,
	ListItemProps,
} from '@app/props';
// Import api models.
import {
	BuildingLimitsCommercialWithGuid,
	BuildingLimitsViewSummary,
} from '@app/models/api';
// Import app models.
import {
	ValidatorResponse,
} from '@app/models/app';
// Import enumerations.
import {
	AppThemeColor,
	Peril,
} from '@app/enumerations';

interface LimitValidationParameters {
	limitToValidate?: number;
	limitToValidateLabel: string;
	maxLimit?: number;
	maxLimitLabel?: string;
	maxLimitInclusive?: boolean;
	minLimit?: number;
	minLimitLabel?: string;
	minLimitInclusive?: boolean;
}

interface UseInputBuildingCoverageLimitsParameters {
	id: string;
	disabled: boolean;
	required: boolean;
	buildingLimitsViewSummaries: BuildingLimitsViewSummary[];
	showValidation?: boolean;
}

interface UseInputBuildingCoverageLimitsResults {
	isDisabled: boolean;
	specifiedBuildingsCoverageLimits: BuildingLimitsCommercialWithGuid[];
	focusToFirstBuildingCoverageLimitInput: (buildingGuid: string) => void;
	checkValidation: (focusToElement: boolean) => boolean;
	changeShowValidationFlag: (showValidation: boolean) => void;
	doBuildingsCoverageLimitsHaveChanges: () => boolean;
	applyBuildingCoverageLimitsListItemProps: (buildingGuid: string) => ListItemProps | undefined
	applyBuildingLimitProps: (buildingGuid: string) => InputCurrencyWithoutInputTextBoxProps | undefined;
	applyContentsLimitProps: (buildingGuid: string) => InputCurrencyWithoutInputTextBoxProps | undefined;
	applyBusinessIncomeExtraExpenseLimitProps: (buildingGuid: string) => InputCurrencyWithoutInputTextBoxProps | undefined;
	getBuildingLimitAsCurrencyFormat: (buildingGuid: string) => string | undefined;
	getContentsLimitAsCurrencyFormat: (buildingGuid: string) => string | undefined;
	getBusinessIncomeExtraExpenseLimitAsCurrencyFormat: (buildingGuid: string) => string | undefined;
	getBuildingLimitLabel: () => string;
	getContentsLimitLabel: () => string;
	getBusinessIncomeExtraExpenseLimitLabel: () => string;
}

const useInputBuildingCoverageLimits = (params: UseInputBuildingCoverageLimitsParameters): UseInputBuildingCoverageLimitsResults => {

	// #region exported methods

	/**
	 * Focuses to the first building coverage limit input. 
	 **/
	const focusToFirstBuildingCoverageLimitInput = (buildingGuid: string): void => {
		if (queuedElementIdToFocus === undefined) {
			let coverageLimitElementId: string = generateIdForBuildingLimitElement(buildingGuid);
			setQueuedElementIdToFocus(coverageLimitElementId);
		}
	};

	/**
	 * Method to check form validation. 
	 **/
	const checkValidation = (focusToElement: boolean): boolean => {
		// Validate all the specified building coverage limits.
		return specifiedBuildingsCoverageLimits.every((specifiedBuildingCoverageLimits: BuildingLimitsCommercialWithGuid) => {
			return validateBuildingLimit(specifiedBuildingCoverageLimits, focusToElement)
				&& validateContentsLimit(specifiedBuildingCoverageLimits, focusToElement)
				&& validateBusinessIncomeExtraExpenseLimit(specifiedBuildingCoverageLimits, focusToElement);
		});
	}

	/**
	 * Sets the show validation flag in the state.
	 * @param showValidation
	 */
	const changeShowValidationFlag = (showValidation: boolean): void => {
		setShowValidation(showValidation);
	}

	/**
	 * Checks to see if the buildings coverage limits have changes. 
	 **/
	const doBuildingsCoverageLimitsHaveChanges = (): boolean => {
		return hasChanges;
	}

	/**
	 * Applies the list item props for the building coverage limits list item.
	 * @param buildingGuid
	 */
	const applyBuildingCoverageLimitsListItemProps = (buildingGuid: string): ListItemProps | undefined => {
		let props: ListItemProps | undefined = undefined;
		// Fetch the matching specified building limits view summary.
		let matchingBuildingLimitsViewSummary: BuildingLimitsViewSummary | undefined = getMatchingBuildingLimitsViewSummary(buildingGuid);
		if (matchingBuildingLimitsViewSummary !== undefined) {
			// Generate the element id for the building coverage limits list item.
			let buildingCoverageLimitsListItemId: string = generateIdForBuildingCoverageLimitsListItem(buildingGuid);
			// Assign the prop values.
			props = {
				id: buildingCoverageLimitsListItemId,
				headerTitle: matchingBuildingLimitsViewSummary.buildingDescription ?? String.empty(),
				subtitle: matchingBuildingLimitsViewSummary.buildingAddress ?? String.empty(),
				mobileIcon: Domain,
				listItemType: AppThemeColor.PRIMARY,
				listItemTypeMobile: AppThemeColor.INFO,
				indentWithMobileIcon: true,
				listItemRef: (ref: React.RefObject<HTMLDivElement>) => registerInputRef(buildingCoverageLimitsListItemId, ref),
			};
		}

		return props;
	}

	/**
	 * Applies the currency input props for the building limit.
	 * @param buildingGuid
	 */
	const applyBuildingLimitProps = (buildingGuid: string): InputCurrencyWithoutInputTextBoxProps | undefined => {
		let props: InputCurrencyWithoutInputTextBoxProps | undefined = undefined;
		// Fetch the matching specified building coverage limits.
		let matchingSpecifiedBuildingCoverageLimits: BuildingLimitsCommercialWithGuid | undefined = getMatchingSpecifiedBuildingCoverageLimits(buildingGuid);
		if (matchingSpecifiedBuildingCoverageLimits !== undefined) {
			// Validate building limit.
			let validationResult: ValidatorResponse = validateBuildingLimitValue(matchingSpecifiedBuildingCoverageLimits)
			// Generate the element id for the building limit.
			let buildingLimitElementId: string = generateIdForBuildingLimitElement(buildingGuid);
			// Assign the prop values.
			props = {
				id: buildingLimitElementId,
				label: getBuildingLimitLabel(),
				hasErrors: showValidation ? !validationResult.isValid : false,
				helperText: showValidation ? validationResult.message : undefined,
				isOptional: !params.required,
				disabled: params.disabled,
				inputRef: (ref: React.RefObject<any>) => registerInputRef(buildingLimitElementId, ref),
				onChange: (value: number | undefined) => changeBuildingLimit(buildingGuid, value),
				value: matchingSpecifiedBuildingCoverageLimits.building ?? undefined,
			};
		}

		return props;
	}

	/**
	 * Applies the currency input props for the contents limit.
	 * @param buildingGuid
	 */
	const applyContentsLimitProps = (buildingGuid: string): InputCurrencyWithoutInputTextBoxProps | undefined => {
		let props: InputCurrencyWithoutInputTextBoxProps | undefined = undefined;
		// Fetch the matching specified building coverage limits.
		let matchingSpecifiedBuildingCoverageLimits: BuildingLimitsCommercialWithGuid | undefined = getMatchingSpecifiedBuildingCoverageLimits(buildingGuid);
		if (matchingSpecifiedBuildingCoverageLimits !== undefined) {
			// Validate contents limit.
			let validationResult: ValidatorResponse = validateContentsLimitValue(matchingSpecifiedBuildingCoverageLimits)
			// Generate the element id for the contents limit.
			let contentsLimitElementId: string = generateIdForContentsLimitElement(buildingGuid);
			// Assign the prop values.
			props = {
				id: contentsLimitElementId,
				label: getContentsLimitLabel(),
				hasErrors: showValidation ? !validationResult.isValid : false,
				helperText: showValidation ? validationResult.message : undefined,
				isOptional: !params.required,
				disabled: params.disabled,
				inputRef: (ref: React.RefObject<any>) => registerInputRef(contentsLimitElementId, ref),
				onChange: (value: number | undefined) => changeContentsLimit(buildingGuid, value),
				value: matchingSpecifiedBuildingCoverageLimits.contents ?? undefined,
			};
		}

		return props;
	}

	/**
	 * Applies the currency input props for the business income extra expense limit.
	 * @param buildingGuid
	 */
	const applyBusinessIncomeExtraExpenseLimitProps = (buildingGuid: string): InputCurrencyWithoutInputTextBoxProps | undefined => {
		let props: InputCurrencyWithoutInputTextBoxProps | undefined = undefined;
		// Fetch the matching specified building coverage limits.
		let matchingSpecifiedBuildingCoverageLimits: BuildingLimitsCommercialWithGuid | undefined = getMatchingSpecifiedBuildingCoverageLimits(buildingGuid);
		if (matchingSpecifiedBuildingCoverageLimits !== undefined) {
			// Validate business income extra expense limit.
			let validationResult: ValidatorResponse = validateBusinessIncomeExtraExpenseLimitValue(matchingSpecifiedBuildingCoverageLimits)
			// Generate the element id for the business income extra expense limit.
			let businessIncomeExtraExpenseLimitElementId: string = generateIdForBusinessIncomeExtraExpenseLimitElement(buildingGuid);
			// Assign the prop values.
			props = {
				id: businessIncomeExtraExpenseLimitElementId,
				label: getBusinessIncomeExtraExpenseLimitLabel(),
				hasErrors: showValidation ? !validationResult.isValid : false,
				helperText: showValidation ? validationResult.message : undefined,
				isOptional: !params.required,
				disabled: params.disabled,
				inputRef: (ref: React.RefObject<any>) => registerInputRef(businessIncomeExtraExpenseLimitElementId, ref),
				onChange: (value: number | undefined) => changeBusinessIncomeExtraExpenseLimitLimit(buildingGuid, value),
				value: matchingSpecifiedBuildingCoverageLimits.businessIncomeExtraExpense ?? undefined,
			};
		}

		return props;
	}

	/**
	 * Fetches the building limit as currency format string.
	 * @param buildingGuid
	 */
	const getBuildingLimitAsCurrencyFormat = (buildingGuid: string): string | undefined => {
		let buildingAsCurrencyFormat: string | undefined = undefined;
		// Fetch the matching specified building coverage limits.
		let matchingSpecifiedBuildingCoverageLimits: BuildingLimitsCommercialWithGuid | undefined = getMatchingSpecifiedBuildingCoverageLimits(buildingGuid);
		if (matchingSpecifiedBuildingCoverageLimits !== undefined
			&& matchingSpecifiedBuildingCoverageLimits.building !== null) {
			// Update the numeric value in currency format.
			buildingAsCurrencyFormat = StringService.getInstance().toCurrencyString(matchingSpecifiedBuildingCoverageLimits.building);
		}
		return buildingAsCurrencyFormat;
	}

	/**
	 * Fetches the contents limit as currency format string.
	 * @param buildingGuid
	 */
	const getContentsLimitAsCurrencyFormat = (buildingGuid: string): string | undefined => {
		let contentsAsCurrencyFormat: string | undefined = undefined;
		// Fetch the matching specified building coverage limits.
		let matchingSpecifiedBuildingCoverageLimits: BuildingLimitsCommercialWithGuid | undefined = getMatchingSpecifiedBuildingCoverageLimits(buildingGuid);
		if (matchingSpecifiedBuildingCoverageLimits !== undefined
			&& matchingSpecifiedBuildingCoverageLimits.contents !== null) {
			// Update the numeric value in currency format.
			contentsAsCurrencyFormat = StringService.getInstance().toCurrencyString(matchingSpecifiedBuildingCoverageLimits.contents);
		}
		return contentsAsCurrencyFormat;
	}

	/**
	 * Fetches the business income extra expense limit as currency format string.
	 * @param buildingGuid
	 */
	const getBusinessIncomeExtraExpenseLimitAsCurrencyFormat = (buildingGuid: string): string | undefined => {
		let businessIncomeExtraExpenseAsCurrencyFormat: string | undefined = undefined;
		// Fetch the matching specified building coverage limits.
		let matchingSpecifiedBuildingCoverageLimits: BuildingLimitsCommercialWithGuid | undefined = getMatchingSpecifiedBuildingCoverageLimits(buildingGuid);
		if (matchingSpecifiedBuildingCoverageLimits !== undefined
			&& matchingSpecifiedBuildingCoverageLimits.businessIncomeExtraExpense !== null) {
			// Update the numeric value in currency format.
			businessIncomeExtraExpenseAsCurrencyFormat = StringService.getInstance().toCurrencyString(matchingSpecifiedBuildingCoverageLimits.businessIncomeExtraExpense);
		}
		return businessIncomeExtraExpenseAsCurrencyFormat;
	}

	/**
	 * Fetches the building limit label.
	 */
	const getBuildingLimitLabel = (): string => 'Building Limit';

	/**
	 * Fetches the contents limit label.
	 */
	const getContentsLimitLabel = (): string => 'Contents Limit';

	/**
	 * Fetches the building limit label.
	 */
	const getBusinessIncomeExtraExpenseLimitLabel = (): string => 'Business Income Extra Expense Limit';

	// #endregion exported methods


	// #region private methods

	/**
	 * Fetches a matching building limits view summary.
	 * @param buildingGuid
	 */
	const getMatchingBuildingLimitsViewSummary = (buildingGuid: string): BuildingLimitsViewSummary | undefined => {
		return params.buildingLimitsViewSummaries.find((buildingLimitsViewSummary: BuildingLimitsViewSummary) => {
			return buildingLimitsViewSummary.buildingGuid === buildingGuid;
		});
	}

	/**
	 * Fetches the current replacement cost value for building.
	 * @param buildingGuid
	 */
	const getCurrentReplacementCostValueForBuilding = (buildingGuid: string): number | undefined => {
		return getMatchingBuildingLimitsViewSummary(buildingGuid)?.buildingReplacementCostValue ?? undefined;
	}

	/**
	 * Fetches a matching specified building coverage limits.
	 * @param buildingGuid
	 */
	const getMatchingSpecifiedBuildingCoverageLimits = (buildingGuid: string): BuildingLimitsCommercialWithGuid | undefined => {
		return specifiedBuildingsCoverageLimits.find((specifiedBuildingCoverageLimits: BuildingLimitsCommercialWithGuid) => {
			return specifiedBuildingCoverageLimits.buildingGuid === buildingGuid;
		});
	}

	/**
	 * Checks to see if all building limit inputs should be disabled. 
	 **/
	const areBuildingLimitInputsDisabled = (): boolean => {
		return params.disabled;
	}

	/**
	 * Checks to see if all contents limit inputs should be disabled. 
	 **/
	const areContentsLimitInputsDisabled = (): boolean => {
		return params.disabled;
	}

	/**
	 * Checks to see if all business income extra expense limit inputs should be disabled. 
	 **/
	const areBusinessIncomeExtraExpenseLimitInputsDisabled = (): boolean => {
		return params.disabled;
	}

	// #region validation methods with scrolling

	/**
	 * Validates the building limit.
	 * @param buildingLimits
	 */
	const validateBuildingLimit = (buildingLimits: BuildingLimitsCommercialWithGuid, focusToElement: boolean): boolean => {
		let isValid: boolean = validateBuildingLimitValue(buildingLimits).isValid;
		if (!isValid
			&& focusToElement
			&& buildingLimits.buildingGuid !== null) {
			let buildingLimitElementId: string = generateIdForBuildingLimitElement(buildingLimits.buildingGuid);
			// Focus on input.
			focusToBuildingCoverageLimitInputElement(buildingLimitElementId);
		}
		return isValid;
	}

	/**
	 * Validates the contents limit.
	 * @param buildingLimits
	 */
	const validateContentsLimit = (buildingLimits: BuildingLimitsCommercialWithGuid, focusToElement: boolean): boolean => {
		let isValid: boolean = validateContentsLimitValue(buildingLimits).isValid;
		if (!isValid
			&& focusToElement
			&& buildingLimits.buildingGuid !== null) {
			let contentsLimitElementId: string = generateIdForContentsLimitElement(buildingLimits.buildingGuid);
			// Focus on input.
			focusToBuildingCoverageLimitInputElement(contentsLimitElementId);
		}
		return isValid;
	}

	/**
	 * Validates the business income extra expense limit.
	 * @param buildingLimits
	 */
	const validateBusinessIncomeExtraExpenseLimit = (buildingLimits: BuildingLimitsCommercialWithGuid, focusToElement: boolean): boolean => {
		let isValid: boolean = validateBusinessIncomeExtraExpenseLimitValue(buildingLimits).isValid;
		if (!isValid
			&& focusToElement
			&& buildingLimits.buildingGuid !== null) {
			let businessIncomeExtraExpenseElementId: string = generateIdForBusinessIncomeExtraExpenseLimitElement(buildingLimits.buildingGuid);
			// Focus on input.
			focusToBuildingCoverageLimitInputElement(businessIncomeExtraExpenseElementId);
		}
		return isValid;
	}

	// #endregion validation methods with scrolling

	// #region validation methods

	/**
	 * Validates the building limit value.
	 * @param buildingLimits
	 */
	const validateBuildingLimitValue = (buildingLimits: BuildingLimitsCommercialWithGuid): ValidatorResponse => {
		// Building limit cannot be greater than the replacement cost value.
		let maxLimit: number | undefined = buildingLimits.buildingGuid !== null
			? getCurrentReplacementCostValueForBuilding(buildingLimits.buildingGuid)
			: undefined;

		return validateLimit({
			limitToValidate: buildingLimits.building ?? undefined,
			limitToValidateLabel: 'building limit',
			minLimit: 0,
			maxLimit: maxLimit,
			maxLimitInclusive: true,
			maxLimitLabel: 'replacement cost value',
		}, areBuildingLimitInputsDisabled(), params.required);
	}

	/**
	 * Validates the contents limit value.
	 * @param buildingLimits
	 */
	const validateContentsLimitValue = (buildingLimits: BuildingLimitsCommercialWithGuid): ValidatorResponse => {
		return validateLimit({
			limitToValidate: buildingLimits.contents ?? undefined,
			limitToValidateLabel: 'contents limit',
			minLimit: 0,
			minLimitInclusive: true,
		}, areContentsLimitInputsDisabled(), params.required);
	}

	/**
	 * Validates the business income extra expense limit value.
	 * @param buildingLimits
	 */
	const validateBusinessIncomeExtraExpenseLimitValue = (buildingLimits: BuildingLimitsCommercialWithGuid): ValidatorResponse => {
		return validateLimit({
			limitToValidate: buildingLimits.businessIncomeExtraExpense ?? undefined,
			limitToValidateLabel: 'business income extra expense limit',
			minLimit: 0,
			minLimitInclusive: true,
		}, areBusinessIncomeExtraExpenseLimitInputsDisabled(), params.required);
	}

	/**
	 * Validates the selected peril deductible.
	 * @param validationParams
	 * @param disabled
	 */
	const validateLimit = (validationParams: LimitValidationParameters, disabled: boolean, required: boolean): ValidatorResponse => {
		let response: ValidatorResponse = new ValidatorResponse(true, String.empty());

		if (!disabled) {
			if (required
				&& validationParams.limitToValidate === undefined) {
				response = new ValidatorResponse(false, `The ${validationParams.limitToValidateLabel} is required.`);
			}
			else if (validationParams.limitToValidate !== undefined){
				if (validationParams.maxLimit !== undefined) {
					// In the case where the max limit is exceeded.
					let maxLimitCurrenyFormat: string = StringService.getInstance().toCurrencyString(validationParams.maxLimit);
					let maxLimitLabel: string = !String.isNullOrWhiteSpace(validationParams.maxLimitLabel)
						? `the ${validationParams.maxLimitLabel} of ${maxLimitCurrenyFormat}`
						: maxLimitCurrenyFormat;

					if ((validationParams.maxLimitInclusive ?? false) && validationParams.limitToValidate > validationParams.maxLimit) {
						response = new ValidatorResponse(false, `The ${validationParams.limitToValidateLabel} cannot be greater than ${maxLimitLabel}.`);
					}
					else if (!validationParams.maxLimitInclusive && validationParams.limitToValidate >= validationParams.maxLimit) {
						response = new ValidatorResponse(false, `The ${validationParams.limitToValidateLabel} cannot be greater than or equal to ${maxLimitLabel}.`);
					}
				}
				if (validationParams.minLimit !== undefined) {
					// In the case where the min limit is exceeded.
					let minLimitCurrenyFormat: string = StringService.getInstance().toCurrencyString(validationParams.minLimit);
					let minLimitLabel: string = !String.isNullOrWhiteSpace(validationParams.minLimitLabel)
						? `the ${validationParams.minLimitLabel} of ${minLimitCurrenyFormat}`
						: minLimitCurrenyFormat;

					if ((validationParams.minLimitInclusive ?? false) && validationParams.limitToValidate < validationParams.minLimit) {
						response = new ValidatorResponse(false, `The ${validationParams.limitToValidateLabel} cannot be less than ${minLimitLabel}.`);
					}
					else if (!validationParams.minLimitInclusive && validationParams.limitToValidate <= validationParams.minLimit) {
						response = new ValidatorResponse(false, `The ${validationParams.limitToValidateLabel} cannot be less than or equal to ${minLimitLabel}.`);
					}
				}
			}
		}
		return response;
	}

	// #endregion validation methods.

	/**
	 * Focus to the building coverage limit input element. 
	 **/
	const focusToBuildingCoverageLimitInputElement = (elementId: string): void => {
		if (queuedElementIdToFocus === undefined) {
			setQueuedElementIdToFocus(elementId);
		}
	};

	// #region input update methods.

	/**
	 * Saves the building limit.
	 * @param buildingGuid
	 * @param buildingLimit
	 */
	const changeBuildingLimit = (buildingGuid: string, buildingLimit: number | undefined): void => {
		setSpecifiedBuildingsCoverageLimits((currentSpecifiedBuildingsCoverageLimits: BuildingLimitsCommercialWithGuid[]) => {
			// Create copy of the current specified buildings coverage limits.
			let copiedSpecifiedBuildingsCoverageLimits: BuildingLimitsCommercialWithGuid[] = [...currentSpecifiedBuildingsCoverageLimits];
			// For each selected specified building coverage limits.
			return copiedSpecifiedBuildingsCoverageLimits.map((copiedSpecifiedBuildingCoverageLimits: BuildingLimitsCommercialWithGuid) => {
				if (copiedSpecifiedBuildingCoverageLimits.buildingGuid === buildingGuid) {
					copiedSpecifiedBuildingCoverageLimits.building = buildingLimit ?? null;
				}
				return copiedSpecifiedBuildingCoverageLimits;
			});
		});
		// Any updates should flag the inputs of having changes.
		setHasChanges(true);
	}

	/**
	 * Saves the contents limit.
	 * @param buildingGuid
	 * @param contentsLimit
	 */
	const changeContentsLimit = (buildingGuid: string, contentsLimit: number | undefined): void => {
		setSpecifiedBuildingsCoverageLimits((currentSpecifiedBuildingsCoverageLimits: BuildingLimitsCommercialWithGuid[]) => {
			// Create copy of the current specified buildings coverage limits.
			let copiedSpecifiedBuildingsCoverageLimits: BuildingLimitsCommercialWithGuid[] = [...currentSpecifiedBuildingsCoverageLimits];
			// For each selected specified building coverage limits.
			return copiedSpecifiedBuildingsCoverageLimits.map((copiedSpecifiedBuildingCoverageLimits: BuildingLimitsCommercialWithGuid) => {
				if (copiedSpecifiedBuildingCoverageLimits.buildingGuid === buildingGuid) {
					copiedSpecifiedBuildingCoverageLimits.contents = contentsLimit ?? null;
				}
				return copiedSpecifiedBuildingCoverageLimits;
			});
		});
		// Any updates should flag the inputs of having changes.
		setHasChanges(true);
	}

	/**
	 * Saves the business income extra expense limit.
	 * @param buildingGuid
	 * @param businessIncomeExtraExpenseLimit
	 */
	const changeBusinessIncomeExtraExpenseLimitLimit = (buildingGuid: string, businessIncomeExtraExpenseLimit: number | undefined): void => {
		setSpecifiedBuildingsCoverageLimits((currentSpecifiedBuildingsCoverageLimits: BuildingLimitsCommercialWithGuid[]) => {
			// Create copy of the current specified buildings coverage limits.
			let copiedSpecifiedBuildingsCoverageLimits: BuildingLimitsCommercialWithGuid[] = [...currentSpecifiedBuildingsCoverageLimits];
			// For each selected specified building coverage limits.
			return copiedSpecifiedBuildingsCoverageLimits.map((copiedSpecifiedBuildingCoverageLimits: BuildingLimitsCommercialWithGuid) => {
				if (copiedSpecifiedBuildingCoverageLimits.buildingGuid === buildingGuid) {
					copiedSpecifiedBuildingCoverageLimits.businessIncomeExtraExpense = businessIncomeExtraExpenseLimit ?? null;
				}
				return copiedSpecifiedBuildingCoverageLimits;
			});
		});
		// Any updates should flag the inputs of having changes.
		setHasChanges(true);
	}

	// #endregion input update methods.

	/**
	 * Registers the input ref.
	 * @param buildingGuid
	 * @param ref
	 */
	const registerInputRef = (elementId: string, ref: React.RefObject<any>): void => {
		let inputRef: React.RefObject<any> | undefined = inputRefs.get(elementId);
		if (inputRef === undefined
			|| inputRef.current === null) {
			setInputRefs((currentInputRefs: Map<string, React.RefObject<any>>) => {
				let copiedInputRefs: Map<string, React.RefObject<any>> = new Map<string, React.RefObject<any>>(currentInputRefs);
				copiedInputRefs.set(elementId, ref);
				return copiedInputRefs;
			});
		}
	}

	// #region id generators

	/**
	 * Generates id. 
	 **/
	const generateId = (): string => {
		return params.id;
	}

	/**
	 * Generates id for building coverage limits list item.
	 * @param buildingGuid
	 */
	const generateIdForBuildingCoverageLimitsListItem = (buildingGuid: string): string => {
		return `${generateId()}-buildingCoverageLimitsListItem-${buildingGuid}`;
	}

	/**
	 * Generates id for building limit.
	 * @param buildingGuid
	 */
	const generateIdForBuildingLimitElement = (buildingGuid: string): string => {
		return `${generateId()}-buildingLimit-${buildingGuid}`;
	}

	/**
	 * Generates id for contents limit.
	 * @param buildingGuid
	 */
	const generateIdForContentsLimitElement = (buildingGuid: string): string => {
		return `${generateId()}-contentsLimit-${buildingGuid}`;
	}

	/**
	 * Generates id for business income extra expense limit.
	 * @param buildingGuid
	 */
	const generateIdForBusinessIncomeExtraExpenseLimitElement = (buildingGuid: string): string => {
		return `${generateId()}-businessIncomeExtraExpense-${buildingGuid}`;
	}

	// #endregion id generators

	// #region initializers

	const setupInitialBuildingLimits = (): BuildingLimitsCommercialWithGuid[] => {
		return params.buildingLimitsViewSummaries.map((buildingLimitsViewSummary: BuildingLimitsViewSummary): BuildingLimitsCommercialWithGuid => {
			return {
				buildingGuid: buildingLimitsViewSummary.buildingGuid,
				building: buildingLimitsViewSummary.building,
				contents: buildingLimitsViewSummary.contents,
				businessIncomeExtraExpense: buildingLimitsViewSummary.businessIncomeExtraExpense
			};
		});
	}

	// #endregion intializers

	// #endregion private methods


	// #region variables

	const [specifiedBuildingsCoverageLimits, setSpecifiedBuildingsCoverageLimits] = React.useState<BuildingLimitsCommercialWithGuid[]>(setupInitialBuildingLimits());
	const [showValidation, setShowValidation] = React.useState<boolean>(params.showValidation ?? false);
	const [hasChanges, setHasChanges] = React.useState<boolean>(false);
	const [inputRefs, setInputRefs] = React.useState<Map<string, React.RefObject<any>>>(new Map<Peril, React.RefObject<any>>());
	const [queuedElementIdToFocus, setQueuedElementIdToFocus] = React.useState<string | undefined>(undefined);

	// #region cached variables

	const isDisabled: boolean = React.useMemo(() => params.disabled, [params.disabled]);

	// #endregion cached variables

	// #endregion variables


	// #region life-cycle methods.

	// Called when input ref or when the focus-to flag is updated.
	React.useEffect(() => {
		// If there is a queued input focus-to flag and the input reference is ready, then proceed to focus on the input.
		if (queuedElementIdToFocus
			&& inputRefs.has(queuedElementIdToFocus)) {
			// Find the matching input ref.
			let inputRef: React.RefObject<any> | undefined = inputRefs.get(queuedElementIdToFocus);
			if (inputRef !== undefined
				&& inputRef.current !== null) {
				// If input is already focused, then do not attempt to do it again.
				if (document.activeElement !== inputRef.current) {
					inputRef.current.focus();
				}
				// Reset the queued input to focus.
				setQueuedElementIdToFocus(undefined);
			}
		}
	}, [queuedElementIdToFocus, inputRefs]);

	// #endregion life-cycle methods.


	return {
		isDisabled,
		specifiedBuildingsCoverageLimits,
		focusToFirstBuildingCoverageLimitInput,
		checkValidation,
		changeShowValidationFlag,
		doBuildingsCoverageLimitsHaveChanges,
		applyBuildingCoverageLimitsListItemProps,
		applyBuildingLimitProps,
		applyContentsLimitProps,
		applyBusinessIncomeExtraExpenseLimitProps,
		getBuildingLimitAsCurrencyFormat,
		getContentsLimitAsCurrencyFormat,
		getBusinessIncomeExtraExpenseLimitAsCurrencyFormat,
		getBuildingLimitLabel,
		getContentsLimitLabel,
		getBusinessIncomeExtraExpenseLimitLabel,
	};
}

// Export parameters.
export {
	UseInputBuildingCoverageLimitsParameters,
}

// Export results.
export {
	UseInputBuildingCoverageLimitsResults,
}

export default useInputBuildingCoverageLimits;